import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Observable, map, startWith } from 'rxjs';
import { randomString } from '../../../lib/random-string';
import { sleep } from '../../../lib/sleep';
import { InputPostDirective } from '../../../shared-extra/directives/input-post.directive';
import { InputPreDirective } from '../../../shared-extra/directives/input-pre.directive';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { InputFieldErrorComponent } from '../input-field-error/input-field-error.component';

@Component({
  selector: 'app-input-field',
  templateUrl: './input-field.component.html',
  styleUrls: ['./input-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [InputFieldErrorComponent, NgTemplateOutlet, LoadingIndicatorComponent, AsyncPipe],
})
export class InputFieldComponent implements OnInit, AfterViewInit {
  private check = inject(ChangeDetectorRef);
  @Input() control: AbstractControl;
  @Input() noLabel = false;
  @Input() bottomFlat = false;
  @Input() topFlat = false;
  @Input() rightFlat = false;
  @Input() leftFlat = false;
  @Input() huge = false;
  @Input() big = false;
  @Input() bigish = false;
  @Input() centeredBigIcon = false;
  @Input() smallNoLabel = false;
  @Input() verySmallNoLabel = false;
  @Input() noLabelExtraSpacing = false;
  @Input() noPreSpacing = false;
  @Input() noError = false;
  @Input() rounded = false;
  @Input() loading = false;
  @ViewChild('input') input: ElementRef;
  @ContentChild(InputPreDirective, { read: TemplateRef }) preTemplate: TemplateRef<unknown>;
  @ContentChild(InputPostDirective, { read: TemplateRef }) postTemplate: TemplateRef<unknown>;
  name: string;
  required: boolean;
  hasPre: boolean;
  hasPost: boolean;
  blockTransitions = true;
  hidden = true;
  notEmpty$: Observable<boolean>;

  async ngOnInit() {
    this.notEmpty$ =
      this.control?.valueChanges.pipe(
        startWith(this.control.value),
        map(value => !!value)
      ) || new Observable();

    this.required = this.isRequired;
    await sleep(0);
    this.hasPre = !!this.preTemplate;
    this.hasPost = !!this.postTemplate;
    this.hidden = false;
    this.check.detectChanges();
    await sleep(100);
    this.blockTransitions = false;
    this.check.detectChanges();
  }

  ngAfterViewInit() {
    const input = this.input.nativeElement.querySelector('input') || this.input.nativeElement.querySelector('select');
    const label = this.input.nativeElement.querySelector('label');
    if (input && label) {
      let id = input.id;
      if (!id) {
        id = randomString(18);
        input.setAttribute('id', id);
      }
      if (label.getAttribute('for') !== id) label.setAttribute('for', id);
    }
  }

  private get isRequired() {
    if (!this.control) return false;
    const validator = this.control.validator?.({} as AbstractControl);
    if (validator?.required) return true;
  }
}
